// src/pages/LoginPage.jsx
import React, { useState, useEffect, useContext, useMemo, useCallback, useRef } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import Paper from "@mui/material/Paper";
import CircularProgress from "@mui/material/CircularProgress";
import Link from "@mui/material/Link";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { AuthContext } from "../../context/AuthContext";
import { useLocation, useNavigate, Link as RouterLink } from "react-router-dom";
import { loginApi, setBearerToken, fetchData } from "../../api/client";
// fetchData ainda usado em reset de senha
import { useTheme } from "@mui/material/styles";

// import logo from "../../assets/logo.png";
// import logowhite from "../../assets/logowhite.png";
import { useBrand } from "../../context/BrandContext";

import SmartWorkspaceInline from "../../components/SmartWorkspaceInline";
import { usePublicSignupAvailability } from "../../hooks/usePublicSignupAvailability";
import { readStoredLocale, setUiLocale, getGlobalLocalesForUiSelect } from "../../i18n/config";
import { parseUiLocaleFromSearch } from "../../i18n/urlLocale";
import { getLoginUiStrings, getLoginLocaleLabel } from "../../i18n/loginUiStrings";

const COMPANY_RE = /^[a-zA-Z0-9._-]{2,32}$/; // simples e objetivo
const toSlug = (v) => String(v || "").trim().toLowerCase();

function readLoginLocaleInitial() {
  if (typeof window !== "undefined") {
    const fromUrl = parseUiLocaleFromSearch(window.location.search);
    if (fromUrl) return fromUrl;
  }
  return readStoredLocale() || "pt-BR";
}

const LoginPage = () => {
  const brand = useBrand();

  const [email, setEmail] = useState(localStorage.getItem("savedEmail") || "");
  const biometricId = useMemo(() => localStorage.getItem("biometricId") || "", []);
  const savedEmail = useMemo(() => localStorage.getItem("savedEmail") || "", []);
  const firstLoad = useMemo(() => sessionStorage.getItem("firstLoad") || "", []);
  const [password, setPassword] = useState("");
  const [company, setCompany] = useState(toSlug(localStorage.getItem("company") || ""));
  const [, setCompanyTouched] = useState(false);
  const [, setCompanyError] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [localeChoice, setLocaleChoice] = useState(readLoginLocaleInitial);
  const [footerLocaleOpen, setFooterLocaleOpen] = useState(false);

  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetError, setResetError] = useState(null);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  const [biometricSupported, setBiometricSupported] = useState(false);
  const [biometricCheckDone, setBiometricCheckDone] = useState(false);
  const { login, redirectPath, registerBiometric, biometricLogin } = useContext(AuthContext);
  const [biometricLoginFailed, setBiometricLoginFailed] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const validatePassword = (password) => password.trim().length > 0;
  const location = useLocation();

  // Refs para foco suave
  const companyRef = useRef(null);
  const emailRef = useRef(null);
  const scrollContainerRef = useRef(null);

  const isValidCompany = (c) => COMPANY_RE.test(String(c || "").trim());

  const handleSetCompany = (c) => {
    const v = toSlug(c);
    setCompany(v);
    setCompanyTouched(true);
    if (!isValidCompany(v)) {
      setCompanyError(getLoginUiStrings(localeChoice).smartWorkspaceInvalidHint);
    } else {
      setCompanyError("");
      // salva ao mudar (UX rápida)
      localStorage.setItem("company", v);
    }
  };

  // Aceita ?c=work (sempre minúsculo), valida e já persiste
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const query = params.get("c");
    if (query) handleSetCompany(query);
  }, [location]);

  // ?lang= / ?locale= / … — alinha o select com a URL (UrlLocaleSync já aplica setUiLocale)
  useEffect(() => {
    const code = parseUiLocaleFromSearch(location.search);
    if (code) setLocaleChoice(code);
  }, [location.search]);

  // Se não veio por query, garante normalização do que já existe
  useEffect(() => {
    if (company && isValidCompany(company)) {
      const normalized = toSlug(company);
      if (normalized !== localStorage.getItem("company")) {
        localStorage.setItem("company", normalized);
      }
    }
  }, [company]);

  // Garante que a página de login sempre comece com scroll no topo (evita logo cortada)
  useEffect(() => {
    window.scrollTo(0, 0);
    const el = scrollContainerRef.current;
    if (el) el.scrollTop = 0;
  }, []);

  // Quando aparece mensagem de erro, rola para o topo para logo e alerta ficarem visíveis
  useEffect(() => {
    if (error) {
      window.scrollTo(0, 0);
      const el = scrollContainerRef.current;
      if (el) el.scrollTop = 0;
    }
  }, [error]);

  // Biometria (inalterado)
  useEffect(() => {
    const checkBiometricAvailabilityOnce = async () => {
      if (!window.PublicKeyCredential) return;
      if (biometricCheckDone) return;
      setBiometricSupported(true);
      setBiometricCheckDone(true);

      if (biometricId && firstLoad === "false") {
        try {
          await handleBiometricLogin();
        } catch (err) {
          if (!String(err?.message || "").includes("SecretSessionError")) {
            console.error("Erro inesperado:", err);
          }
        }
      }
    };
    checkBiometricAvailabilityOnce();
  }, [biometricCheckDone, firstLoad, biometricId]);

  const isValidLoginInput = () => {
    // 1) company primeiro (guia o usuário)
    if (!COMPANY_RE.test(String(company || "").trim())) {
      setCompanyTouched(true);
      setCompanyError(getLoginUiStrings(localeChoice).companyFieldInvalidLogin);
      try { companyRef.current?.focus?.(); } catch {}
      return false;
    }
    // 2) email
    if (!validateEmail(email)) {
      setError("Insira um email válido.");
      emailRef.current?.focus?.();
      return false;
    }
    // 3) senha
    if (!validatePassword(password)) {
      setError("Senha obrigatória.");
      return false;
    }
    return true;
  };

  const handleLogin = useCallback(async () => {
    if (!isValidLoginInput()) return;

    // Garante que o company atual esteja salvo (minúsculo)
    const normalized = toSlug(company);
    if (localStorage.getItem("company") !== normalized) {
      localStorage.setItem("company", normalized);
    }

    setLoading(true);
    try {
      setError(null);
      const response = await loginApi(email, password);
      const preferredLocale = String(localeChoice || "pt-BR").trim() || "pt-BR";
      setUiLocale(preferredLocale);
      login(response.data.token, response.data.user || {});
      localStorage.setItem("savedEmail", email);
      !biometricId && handleRegisterBiometric();

      // Mantém o parâmetro c no redirecionamento pós-login
      const qs = normalized ? `?c=${encodeURIComponent(normalized)}` : "";
      navigate((redirectPath || "/") + qs);
    } catch (err) {
      const msg =
        err?.message ||
        err?.response?.data?.message ||
        (Array.isArray(err?.response?.data?.message) && err.response.data.message[0]) ||
        "Não foi possível entrar. Tente novamente.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [email, password, company, redirectPath, localeChoice]);

  // Enviar redefinição de senha (backend usa header company; aqui só aciona endpoint)
  const handleResetPassword = async () => {
    if (!validateEmail(resetEmail)) {
      setResetError("Por favor, insira um email válido.");
      return;
    }
    try {
      setResetError(null);
      setResetSuccess(false);
      setResetLoading(true);

      const payload = { email: resetEmail.trim() };
      await fetchData(`/users/send-reset`, "POST", payload);

      setResetSuccess(true);
    } catch (err) {
      const body = err?.response?.data;
      const msg =
        (typeof body?.message === "string" && body.message) ||
        (Array.isArray(body?.message) && body.message[0]) ||
        err?.message ||
        "Erro ao solicitar redefinição de senha. Tente novamente.";
      setResetError(msg);
    } finally {
      setResetLoading(false);
    }
  };

  const handleRegisterBiometric = async () => {
    try {
      await registerBiometric(email);
    } catch {
      console.error("Erro ao registrar credencial biométrica.");
    }
  };

  const handleBiometricLogin = async () => {
    if (!biometricId || biometricLoginFailed) return;
    try {
      setLoading(true);
      sessionStorage.setItem("firstLoad", "true");
      const response = await biometricLogin(email);
      if (response?.data?.token) {
        setBearerToken(response.data.token);
        const preferredLocale = String(localeChoice || "pt-BR").trim() || "pt-BR";
        const mergedUser = { ...(response.data.user || {}), locale_code: preferredLocale };
        login(response.data.token, mergedUser);
        try {
          await fetchData("/users/me/locale", "PATCH", { locale_code: preferredLocale });
        } catch (localeErr) {
          console.warn("Falha ao persistir locale no perfil:", localeErr?.message || localeErr);
        }
        // preserva c
        const normalized = toSlug(localStorage.getItem("company") || "");
        const qs = normalized ? `?c=${encodeURIComponent(normalized)}` : "";
        navigate((redirectPath || "/") + qs);
      }
    } catch (err) {
      console.error("Erro ao realizar autenticação biométrica:", err);
      setError(err?.message || "Não foi possível entrar com biometria. Use e-mail e senha.");
      setBiometricLoginFailed(true);
    } finally {
      setLoading(false);
    }
  };

  // Enter global: salva workspace se mudou e avança fluxo
  const handleKeyDown = (event) => {
    if (event.key !== "Enter") return;

    // Se o campo de company está inválido, primeiro valida e salva
    if (!isValidCompany(company)) {
      event.preventDefault();
      setCompanyTouched(true);
      setCompanyError(getLoginUiStrings(localeChoice).smartWorkspaceInvalidHint);
      try { companyRef.current?.focus?.(); } catch {}
      return;
    }

    // Sincroniza storage se necessário (minúsculo)
    const normalized = toSlug(company);
    if (localStorage.getItem("company") !== normalized) {
      localStorage.setItem("company", normalized);
    }

    // Se foco está em CompanyField, ao Enter move para e-mail para agilizar
    const active = document.activeElement;
    const isOnCompany =
      active?.getAttribute?.("name") === "company" ||
      active?.id?.toLowerCase?.().includes?.("company");

    if (isOnCompany) {
      event.preventDefault();
      emailRef.current?.focus?.();
      return;
    }

    // Senão, tenta logar (deixa a validação decidir)
    event.preventDefault();
    handleLogin();
  };

  // Banner de orientação quando workspace não está definido/ok
  const showCompanyBanner = !isValidCompany(company);
  const L = getLoginUiStrings(localeChoice);
  const { loading: signupAvailLoading, available: publicSignupAvailable } = usePublicSignupAvailability();

  const handleLocaleChoice = (next) => {
    const locale = String(next || "").trim() || "pt-BR";
    setLocaleChoice(locale);
    setUiLocale(locale);
  };

  return (
    <Box
      ref={scrollContainerRef}
      display="flex"
      flexDirection="column"
      alignItems="center"
      sx={{
        height: "100vh",
        minHeight: "100vh",
        maxHeight: "100vh",
        background:
          theme.palette.mode === "dark"
            ? "linear-gradient(135deg, #121212, #1e1e1e)"
            : "linear-gradient(135deg, #f5f5f5, #e3e3e3)",
        color: theme.palette.text.primary,
        p: 2,
        pt: 4,
        pb: 4,
        overflowY: "auto",
        boxSizing: "border-box",
      }}
      onKeyDown={handleKeyDown}
    >
      <Box display="flex" alignItems="center" justifyContent="center" mb={4} flexShrink={0}>
        <img
          src={theme.palette.mode === "dark" ? brand.assets.logo.dark : brand.assets.logo.light}
          alt={brand.name}
          style={{ height: 60 }}
          loading="lazy"
        />
      </Box>

      <Paper
        elevation={6}
        sx={{
          p: 4,
          maxWidth: 420,
          width: "90%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          borderRadius: 3,
          bgcolor: theme.palette.background.paper,
        }}
      >
        <Avatar sx={{ bgcolor: theme.palette.primary.main, mb: 2 }}>
          <LockOutlinedIcon />
        </Avatar>

        <Typography variant="h5" gutterBottom>
          {savedEmail ? L.welcomeBack : L.welcome}
        </Typography>

        {showCompanyBanner && (
          <Alert
            severity="info"
            sx={{ width: "100%", mb: 2 }}
            onClose={() => setCompanyTouched(true)}
          >
            {L.workspaceBanner}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ width: "100%", mb: 2 }} role="alert">
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
              {L.loginFailed}
            </Typography>
            <Typography variant="body2">{error}</Typography>
          </Alert>
        )}

        <Box component="form" width="100%" display="flex" flexDirection="column" gap={1.25}>
          {/* Company / Workspace */}
          <SmartWorkspaceInline
            value={company}
            onSave={(val) => {
              const v = toSlug(val);
              setCompany(v);
              setCompanyTouched(true);
              setCompanyError(COMPANY_RE.test(v) ? "" : L.companyFieldInvalidLogin);
              if (COMPANY_RE.test(v)) {
                localStorage.setItem("company", v);
              }
            }}
            onEnterNext={() => { try { emailRef.current?.focus?.(); } catch {} }}
            name="company"
            inputRef={companyRef}
            label={L.workspaceField}
            invalidHelperText={L.smartWorkspaceInvalidHint}
          />

          {/* Email */}
          <TextField
            inputRef={emailRef}
            label={L.email}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            margin="dense"
            error={!!error && !validateEmail(email)}
            helperText={!!error && !validateEmail(email) ? L.invalidEmail : undefined}
          />

          {/* Senha + esqueceu a senha (grupo compacto) */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, mt: -0.25 }}>
            <TextField
              label={L.password}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              margin="dense"
              error={!!error && !password}
              helperText={!!error && !password ? L.requiredPassword : undefined}
            />
            <Box sx={{ width: "100%", display: "flex", justifyContent: "flex-end", lineHeight: 1.2 }}>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => {
                  setResetEmail(email || "");
                  setResetError(null);
                  setResetSuccess(false);
                  setResetModalOpen(true);
                }}
                sx={{
                  fontSize: "0.8125rem",
                  cursor: "pointer",
                  textDecoration: "none",
                  color: "text.secondary",
                  border: 0,
                  background: "none",
                  fontFamily: "inherit",
                  p: 0,
                  py: 0.125,
                  "&:hover": { textDecoration: "underline", color: "primary.main" },
                }}
              >
                {L.forgotPassword}
              </Link>
            </Box>
          </Box>

          <Button
            variant="contained"
            color="primary"
            onClick={handleLogin}
            fullWidth
            sx={{
              mt: 0.75,
              height: 48,
              fontWeight: "bold",
              fontSize: "1rem",
              textTransform: "none",
            }}
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} color="inherit" />}
          >
            {loading ? L.loggingIn : L.login}
          </Button>

          {!signupAvailLoading && publicSignupAvailable && (
            <Box sx={{ width: "100%", textAlign: "center", mt: 1.25 }}>
              <Typography
                component="span"
                variant="body2"
                sx={{ fontSize: "0.8125rem", color: "text.secondary" }}
              >
                {L.noAccount}{" "}
              </Typography>
              <Link
                component={RouterLink}
                to="/signup"
                variant="body2"
                sx={{ fontSize: "0.8125rem", fontWeight: 500 }}
              >
                {L.createWorkspaceLink}
              </Link>
            </Box>
          )}
        </Box>

        {biometricSupported && !biometricLoginFailed && biometricId && (
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleBiometricLogin}
            fullWidth
            sx={{ mt: 2 }}
          >
            {L.biometric}
          </Button>
        )}
      </Paper>

      <Box
        mt={4}
        pb={2}
        display="flex"
        flexDirection="column"
        alignItems="center"
        gap={0.75}
        sx={{ flexShrink: 0 }}
      >
        {footerLocaleOpen ? (
          <FormControl size="small" variant="standard" sx={{ minWidth: 200 }}>
            <Select
              open={footerLocaleOpen}
              onClose={() => setFooterLocaleOpen(false)}
              value={localeChoice}
              onChange={(e) => {
                handleLocaleChoice(e.target.value);
                setFooterLocaleOpen(false);
              }}
              autoFocus
              MenuProps={{
                anchorOrigin: { vertical: "top", horizontal: "center" },
                transformOrigin: { vertical: "bottom", horizontal: "center" },
              }}
              sx={{
                fontSize: "0.8125rem",
                color: theme.palette.text.secondary,
                "&::before, &::after": { display: "none" },
                "& .MuiSelect-select": { py: 0.25, pr: "24px !important" },
              }}
            >
              {getGlobalLocalesForUiSelect().map(({ code, nativeName }) => (
                <MenuItem key={code} value={code}>
                  {nativeName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <Typography
            component="button"
            type="button"
            variant="caption"
            onClick={() => setFooterLocaleOpen(true)}
            aria-label={L.localeLabel}
            aria-haspopup="listbox"
            sx={{
              cursor: "pointer",
              color: theme.palette.text.secondary,
              border: 0,
              background: "none",
              font: "inherit",
              p: 0,
              textDecoration: "underline",
              textDecorationColor: theme.palette.action.disabled,
              textUnderlineOffset: 3,
              "&:hover": {
                color: theme.palette.primary.main,
                textDecorationColor: theme.palette.primary.main,
              },
            }}
          >
            {getLoginLocaleLabel(localeChoice)}
          </Typography>
        )}
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, textAlign: "center" }}>
          © {new Date().getFullYear()} {brand.name}. Todos os direitos reservados.
        </Typography>
      </Box>

      {/* Modal de Redefinição */}
      <Dialog open={resetModalOpen} onClose={() => setResetModalOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{L.resetTitle}</DialogTitle>
        <DialogContent>
          <Typography>
            {L.resetHint}
          </Typography>
          <TextField
            label={L.email}
            type="email"
            value={resetEmail}
            onChange={(e) => setResetEmail(e.target.value)}
            fullWidth
            sx={{ mt: 2 }}
            error={!!resetError && !validateEmail(resetEmail)}
            helperText={!!resetError && !validateEmail(resetEmail) ? L.invalidEmail : " "}
          />
          {resetError && (
            <Alert severity="error" sx={{ mt: 2 }} role="alert">
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                {L.resetErrorTitle}
              </Typography>
              <Typography variant="body2">{resetError}</Typography>
            </Alert>
          )}
          {resetSuccess && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {L.resetSuccess}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetModalOpen(false)} disabled={resetLoading}>
            {L.cancel}
          </Button>
          <Button variant="contained" onClick={handleResetPassword} disabled={resetLoading}>
            {resetLoading ? L.sending : L.sendLink}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LoginPage;
